import { createFileRoute, useRouter } from '@tanstack/react-router'

import { SupplierBraLingeriePage, SUPPLIER_BRA_LINGERIE_FAQ, SUPPLIER_BRA_LINGERIE_PATH } from '../components/SupplierBraLingeriePage'
import { getProductPrimaryImage, getStorefrontConfig, listStorefrontCategories, listStorefrontProducts } from '../lib/dropify'
import type { StorefrontCategory, StorefrontProduct } from '../lib/dropify'
import { listDashboardNewsPosts } from '../lib/news'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'

const CATEGORY_SLUG = 'bra-lingerie'

function toCatalogProduct(product: StorefrontProduct) {
  return { name: product.name, slug: product.slug || product.id, sku: product.sku, image: getProductPrimaryImage(product, { width: 760, format: 'webp', quality: 78 }) }
}

export function isLingerieProduct(product: StorefrontProduct) {
  return product.sku.toUpperCase().startsWith('LING-WA-')
}

export const Route = createFileRoute('/supplier-bra-lingerie-surabaya')({
  loader: async () => {
    const [config, categories, first, proofImages, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      listStorefrontProducts({ category: CATEGORY_SLUG, per_page: 100 }).catch(() => null),
      getBrandProofImages().catch(() => []),
      listDashboardNewsPosts().catch(() => []),
    ])
    const rest = first ? await Promise.all(Array.from({ length: Math.max(0, first.total_pages - 1) }, (_, index) => listStorefrontProducts({ category: CATEGORY_SLUG, page: index + 2, per_page: 100 }))).catch(() => []) : []
    const items = first ? [first, ...rest].flatMap((response) => response.items) : []
    const lingerie = items.filter(isLingerieProduct).map(toCatalogProduct)
    const bra = items.filter((product) => !isLingerieProduct(product)).map(toCatalogProduct)
    return { config, categories, braProducts: bra, lingerieProducts: lingerie, totalModels: items.length, productsFailed: first === null, proofImages, newsPosts }
  },
  head: ({ loaderData }) => {
    const title = brandTitle('Supplier Bra & Lingerie Grosir di Surabaya')
    const description = 'Supplier bra dan lingerie grosir di Surabaya. Katalog bra dipisahkan dari lingerie dan sleepwear agar mudah dicari. MOQ 25 lusin, kirim seluruh Indonesia.'
    const heroImage = loaderData?.braProducts[0]?.image || loaderData?.lingerieProducts[0]?.image
    return {
      meta: [{ title }, { name: 'description', content: description }, { name: 'robots', content: INDEXABLE_ROBOTS }, { property: 'og:title', content: title }, { property: 'og:description', content: description }, { property: 'og:url', content: absoluteUrl(SUPPLIER_BRA_LINGERIE_PATH) }, { property: 'og:locale', content: 'id_ID' }, ...(heroImage ? [{ property: 'og:image', content: heroImage }] : []), { name: 'twitter:card', content: 'summary_large_image' }],
      links: [{ rel: 'canonical', href: absoluteUrl(SUPPLIER_BRA_LINGERIE_PATH) }],
      scripts: [
        { type: 'application/ld+json', children: JSON.stringify(buildBreadcrumbSchema([{ name: 'Beranda', path: '/' }, { name: 'Supplier Bra & Lingerie Surabaya', path: SUPPLIER_BRA_LINGERIE_PATH }])) },
        { type: 'application/ld+json', children: JSON.stringify(buildFaqSchema(SUPPLIER_BRA_LINGERIE_FAQ)) },
      ],
    }
  },
  component: SupplierBraLingerieRoute,
})

function SupplierBraLingerieRoute() {
  const router = useRouter()
  const data = Route.useLoaderData()
  return <SupplierBraLingeriePage {...data} onRetry={() => router.invalidate()} />
}
